<!--
  @component ReleaseLifecycleEdge — E2 effecting composition.
  composes: ReleaseIO + ProvenanceIO.
  effects:  start / cutover / drain / rollback (m-arc release phases),
            plus a composed `withCutover()` one-button lifecycle
            (rolling → cutover → drained).
  purpose:  the m-arc release through its phases as a single edge.
            Narrower than DeployRunEdge: this is about ONE release
            already underway, not the broad "do a deploy" flow.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { RELEASE_LIFECYCLE_EDGE_KEY } from "./deployment-edges.js";
  import type {
    ReleaseLifecycleEdgeContext,
    ReleasePhase,
  } from "./deployment-edges.js";
  import type { DeployTargetRef } from "../../nodes/deployment/deployment-io.js";

  export { RELEASE_LIFECYCLE_EDGE_KEY };
  export type { ReleaseLifecycleEdgeContext, ReleasePhase };

  export function useReleaseLifecycleEdge(): ReleaseLifecycleEdgeContext {
    const ctx = getContext<ReleaseLifecycleEdgeContext | undefined>(
      RELEASE_LIFECYCLE_EDGE_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useReleaseLifecycleEdge() called outside of a ReleaseLifecycleEdge tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useReleaseIO } from "../../nodes/deployment/ReleaseIO.svelte";
  import { useProvenanceIO } from "../../nodes/deployment/ProvenanceIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const release = useReleaseIO();
  const provenance = useProvenanceIO();

  let phase: ReleasePhase = $state("idle");
  let target: DeployTargetRef | null = $state(null);
  let lastError: Error | null = $state(null);

  async function runPhase<T>(
    nextPhase: ReleasePhase,
    op: () => Promise<T>,
  ): Promise<T> {
    phase = nextPhase;
    lastError = null;
    try {
      return await op();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      phase = "failed";
      throw err;
    }
  }

  async function start(
    t: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    target = t;
    return runPhase("rolling", () => release.release(t, options));
  }

  async function doCutover(
    t: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    target = t;
    return runPhase("cutover", () => release.cutover(t, options));
  }

  async function drain(
    t: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    target = t;
    return runPhase("drained", () => release.drain(t, options));
  }

  async function rollback(
    t: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    target = t;
    return runPhase("rolledBack", () => release.rollbackApp(t, options));
  }

  async function withCutover(
    t: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    target = t;
    lastError = null;
    try {
      phase = "rolling";
      await release.release(t, options);
      phase = "cutover";
      await release.cutover(t, options);
      phase = "drained";
      return await release.drain(t, options);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      phase = "failed";
      throw err;
    }
  }

  const ctx: ReleaseLifecycleEdgeContext = {
    get phase() {
      return phase;
    },
    get target() {
      return target;
    },
    get lastError() {
      return lastError;
    },
    get inFlight() {
      return release.inFlight;
    },
    get provenance() {
      return provenance.events.filter((e) => e.op.startsWith("release."));
    },
    start,
    cutover: doCutover,
    drain,
    rollback,
    withCutover,
  } satisfies ReleaseLifecycleEdgeContext;

  setContext(RELEASE_LIFECYCLE_EDGE_KEY, ctx);
</script>

{@render children?.()}
